import { coalesceAsync } from 'promise-coalesce';
import * as rxjs from 'rxjs';
import { v4 as uuid } from 'uuid';

/**
 * Resolves with the first non-undefined value returned from the `action` function.
 * Periodically invokes the action until either a value is returned or the timeout is reached.
 */
export const waitUntil = async <T>(options: {
  /**
   * Optional key to coalesce multiple calls to the same action.
   */
  key?: string;
  /**
   * Evaluated each interval until it returns a value or the timeout is reached.
   */
  action: () => Promise<T | undefined>;
  /**
   * How often to evaluate the condition, in milliseconds.
   * Default is 1000ms.
   */
  interval?: number;
  /**
   * How long to wait before stop and return undefined, in milliseconds.
   * Default is 10,000ms.
   */
  timeout?: number;
}): Promise<T | undefined> => {
  const { action, interval = 1_000, timeout = 10_000 } = options;

  const key = options.key ?? uuid();

  return coalesceAsync(key, async () => {
    const poller$ = rxjs.timer(0, interval).pipe(
      rxjs.concatMap(async () => {
        try {
          return await action();
        } catch (error) {
          // do nothing, will retry
        }
      }),
      rxjs.filter((m) => m !== undefined), // check if condition met yet
      rxjs.take(1), // stop poller after conditions met
      rxjs.timeout(timeout), // abort if conditions not met in time
      rxjs.catchError(() => rxjs.of(undefined)) // convert timeout error
    );

    return rxjs.firstValueFrom(poller$); // resolves once a value is emitted
  });
};
